import { type GraphQLTaggedNode } from 'react-relay';
import {
	getRequest,
	type NormalizationLinkedField,
	type NormalizationSelection,
} from 'relay-runtime';

type ResultRecord = Record<string, unknown>;

function isResultRecord(value: unknown): value is ResultRecord {
	return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function getResultKey(selection: NormalizationLinkedField): string {
	return selection.alias ?? selection.name;
}

function addLinkedFieldTypenames(
	value: unknown,
	selection: NormalizationLinkedField,
): { value: unknown; changed: boolean } {
	if (value == null) {
		return { value, changed: false };
	}

	if (selection.plural) {
		if (!Array.isArray(value)) {
			return { value, changed: false };
		}

		let nextItems: unknown[] | null = null;

		value.forEach((item, index) => {
			const result = addLinkedFieldTypenames(item, {
				...selection,
				plural: false,
			});

			if (result.changed) {
				nextItems ??= [...value];
				nextItems[index] = result.value;
			}
		});

		return nextItems ? { value: nextItems, changed: true } : { value, changed: false };
	}

	if (!isResultRecord(value)) {
		return { value, changed: false };
	}

	let nextValue = value;
	let changed = false;

	if (selection.concreteType && nextValue.__typename == null) {
		nextValue = {
			...nextValue,
			__typename: selection.concreteType,
		};
		changed = true;
	}

	const nestedResult = addTypenamesForSelections(nextValue, selection.selections);
	if (nestedResult.changed) {
		nextValue = nestedResult.value;
		changed = true;
	}

	return { value: nextValue, changed };
}

function addTypenamesForSelections(
	data: ResultRecord,
	selections: readonly NormalizationSelection[],
): { value: ResultRecord; changed: boolean } {
	let nextData = data;
	let changed = false;

	selections.forEach((selection) => {
		switch (selection.kind) {
			case 'LinkedField': {
				const key = getResultKey(selection);
				if (!(key in nextData)) return;

				const result = addLinkedFieldTypenames(nextData[key], selection);
				if (!result.changed) return;

				if (!changed) {
					nextData = { ...nextData };
					changed = true;
				}
				nextData[key] = result.value;
				return;
			}
			case 'ActorChange': {
				const key = getResultKey(selection.linkedField);
				if (!(key in nextData)) return;

				const result = addLinkedFieldTypenames(nextData[key], selection.linkedField);
				if (!result.changed) return;

				if (!changed) {
					nextData = { ...nextData };
					changed = true;
				}
				nextData[key] = result.value;
				return;
			}
			case 'ClientExtension':
			case 'Condition':
			case 'Defer':
			case 'InlineFragment':
			case 'Stream': {
				const result = addTypenamesForSelections(nextData, selection.selections);
				if (!result.changed) return;

				nextData = result.value;
				changed = true;
				return;
			}
			case 'ClientComponent': {
				if (!('selections' in selection.fragment)) return;

				const result = addTypenamesForSelections(nextData, selection.fragment.selections);
				if (!result.changed) return;

				nextData = result.value;
				changed = true;
				return;
			}
			default:
				return;
		}
	});

	return { value: nextData, changed };
}

export function addRelayConcreteTypenamesToQueryData<TData>(
	query: GraphQLTaggedNode,
	data: TData,
): TData {
	if (!isResultRecord(data)) {
		return data;
	}

	const request = getRequest(query);
	const result = addTypenamesForSelections(data, request.operation.selections);

	return result.value as TData;
}
